import { EyeOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Table, Tooltip, Button, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import cs from 'classnames';
import { useState } from 'react';
import { DocumentViewerModal } from '@ui/components/DocumentViewerModal';
import type { IPatient, IPatientRecord } from './PatientListSelection';
import type { PatientResponse, Record } from '@packages/types';
import styles from './Styles.module.scss';

interface PatientDocumentListProps {
    patient: PatientResponse | IPatient;
    isIngestionMode?: boolean;
    filterParams?: any;
    selectedShowOnlyMatched?: { [key: string | number]: boolean };
    showMatchedColumn?: boolean; // Whether to show the "Matched" column (default: true)
    showWhereMatchedColumn?: boolean; // When true, add "Match source" column (Silver/Bronze check) to the right of Source
}

/** Strip "Note Source ID " prefix from document names (set by annotations2omop when no title exists). Export for reuse. */
export const formatDocumentName = (name?: string) => {
    if (!name) return '';
    return name.startsWith('Note Source ID ') ? name.replace(/^Note Source ID\s+/, '') : name;
};

const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        // Check for invalid dates or dates before 1900 (epoch/zero dates)
        if (isNaN(date.getTime()) || date.getFullYear() < 1900) {
            return '-';
        }
        return date.toLocaleDateString();
    } catch {
        return '-';
    }
};

export default function PatientDocumentList({
    patient,
    isIngestionMode,
    selectedShowOnlyMatched,
    showMatchedColumn = true,
    showWhereMatchedColumn = false,
}: PatientDocumentListProps) {
    const [viewerState, setViewerState] = useState<{
        open: boolean;
        noteId: number | undefined;
        documentId: string | undefined;
        documentName: string;
    }>({
        open: false,
        noteId: undefined,
        documentId: undefined,
        documentName: '',
    });

    const handleViewDocument = (record: IPatientRecord | Record) => {
        // If it's ingestion mode (Elasticsearch), we likely have a documentId (record.id) but no noteId.
        // If it's standard mode, we have noteId.
        const isElasticsearch = isIngestionMode || record.dataSource === 'raw_extractions' || !record.noteId;

        setViewerState({
            open: true,
            noteId: !isElasticsearch ? Number(record.noteId) : undefined,
            documentId: isElasticsearch ? String(record.id) : undefined,
            documentName: formatDocumentName(record.name) || record.name || '',
        });
    };

    const handleCloseViewer = () => {
        setViewerState({ ...viewerState, open: false });
    };

    const columns: ColumnsType<IPatientRecord | Record> = [
        ...(showMatchedColumn
            ? [
                  {
                      title: 'Matched',
                      key: 'matched',
                      width: 80,
                      align: 'center' as const,
                      render: (_: any, record: any) => {
                          const matched = record.matched === true;
                          const notMatched = record.matched === false;
                          const matchedConditions = record.matchedConditions || [];
                          const tooltipTitle =
                              matchedConditions.length > 0
                                  ? `Matched conditions: ${matchedConditions.join(', ')}`
                                  : matched
                                    ? 'Matched all conditions'
                                    : notMatched
                                      ? 'No conditions matched'
                                      : '';

                          return (
                              <Tooltip title={tooltipTitle}>
                                  <div className="flex justify-center">
                                      {matched ? (
                                          <CheckCircleOutlined className="text-green-600" />
                                      ) : notMatched ? (
                                          <CloseCircleOutlined className="text-gray-400" />
                                      ) : null}
                                  </div>
                              </Tooltip>
                          );
                      },
                  },
              ]
            : []),
        {
            title: 'Document Name',
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
            render: (name: string, record: any) => {
                const matched = record.matched === true;
                const notMatched = record.matched === false;
                const displayName = formatDocumentName(name) || name || '-';
                return (
                    <span
                        className={`font-medium ${
                            matched ? 'font-bold text-gray-900' : notMatched ? 'text-gray-400' : ''
                        }`}
                    >
                        {displayName}
                    </span>
                );
            },
        },
        {
            title: 'Source',
            dataIndex: 'sourceFlags',
            key: 'source',
            width: 100,
            ellipsis: true,
            render: (_: unknown, record: any) => {
                const matched = record.matched === true;
                const notMatched = record.matched === false;
                const sourceFlags = record.sourceFlags as ('omop' | 'es' | 'es-only')[] | undefined;
                const dataSource = record.dataSource as string | undefined;
                const isBronzeDoc = sourceFlags?.includes('es-only') || dataSource === 'raw_extractions';
                const sourceTag = isBronzeDoc ? (
                    <Tag className={cs('!m-0', styles.bronzeTag)}>Bronze</Tag>
                ) : (
                    <Tag className={cs('!m-0', styles.silverTag)}>Silver</Tag>
                );
                return (
                    <span
                        className={`flex flex-wrap gap-0.5 ${
                            matched ? 'font-bold text-gray-900' : notMatched ? 'text-gray-400' : 'text-gray-500'
                        }`}
                    >
                        {record.id || record.name ? sourceTag : <span>-</span>}
                    </span>
                );
            },
        },
        ...(showWhereMatchedColumn
            ? [
                  {
                      title: 'Match source',
                      key: 'matchSource',
                      width: 100,
                      align: 'center' as const,
                      render: (_: unknown, record: any) => {
                          const sourceFlags = record.sourceFlags as ('omop' | 'es' | 'es-only')[] | undefined;
                          const matchInSilver = sourceFlags?.includes('omop');
                          const matchInBronze =
                              sourceFlags?.includes('es') || (sourceFlags?.includes('es-only') && !!record.matched);
                          const matchedConditions =
                              (record.matchedConditions as string[] | undefined)?.filter(Boolean) || [];
                          const filterLabels: { [k: string]: string } = {
                              diagnosis: 'Diagnosis',
                              procedure_type: 'Procedure type',
                              medication_name: 'Medication name',
                              document_type: 'Document type',
                              assertion: 'Assertion',
                              entity: 'Entity',
                              note_type: 'Note type',
                          };
                          const filterText =
                              matchedConditions.length > 0
                                  ? matchedConditions.map(c => filterLabels[c] || c.replace(/_/g, ' ')).join(', ')
                                  : '';
                          const sourceLabel = matchInSilver
                              ? 'Matched in Silver (OMOP)'
                              : matchInBronze
                                ? 'Matched in Bronze (Raw/NLP)'
                                : '';
                          const tooltipTitle =
                              filterText.length > 0 ? (
                                  <span>
                                      {sourceLabel}
                                      <br />
                                      Matched filter(s): {filterText}
                                  </span>
                              ) : (
                                  sourceLabel || undefined
                              );
                          if (matchInSilver) {
                              return (
                                  <Tooltip title={tooltipTitle}>
                                      <CheckCircleOutlined className="text-xs !text-[#8E8E8E]" />
                                  </Tooltip>
                              );
                          }
                          if (matchInBronze) {
                              return (
                                  <Tooltip title={tooltipTitle}>
                                      <CheckCircleOutlined className="text-xs !text-[#CD7F32]" />
                                  </Tooltip>
                              );
                          }
                          return <span className="text-gray-400">-</span>;
                      },
                  },
              ]
            : []),
        {
            title: 'Type',
            dataIndex: 'documentType',
            key: 'type',
            render: (documentType: string, record: any) => {
                const matched = record.matched === true;
                const notMatched = record.matched === false;
                return (
                    <span
                        className={`capitalize ${matched ? 'font-bold text-gray-900' : notMatched ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                        {documentType || record.type}
                    </span>
                );
            },
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (date: string, record: any) => {
                const matched = record.matched === true;
                const notMatched = record.matched === false;
                return (
                    <span
                        className={matched ? 'font-bold text-gray-900' : notMatched ? 'text-gray-400' : 'text-gray-500'}
                    >
                        {formatDate(date)}
                    </span>
                );
            },
        },
        {
            title: '',
            key: 'action',
            render: (_, record) => (
                <Tooltip title="View document">
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        className="text-blue-500 hover:text-blue-700"
                        onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleViewDocument(record);
                        }}
                    />
                </Tooltip>
            ),
        },
    ];

    return (
        <>
            <Table
                dataSource={
                    patient.records?.filter(
                        record => record.matched || !selectedShowOnlyMatched?.[record?.patientId]
                    ) || []
                }
                columns={columns}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ y: 180 }}
                className="[&_.ant-table-thead_.ant-table-cell]:bg-gray-100 [&_.ant-table-thead_.ant-table-cell]:text-xs [&_.ant-table-thead_.ant-table-cell]:font-semibold [&_.ant-table-thead_.ant-table-cell]:text-gray-600"
                onRow={(record: IPatientRecord | Record) => {
                    const matched = (record as any).matched === true;
                    const notMatched = (record as any).matched === false;
                    return {
                        onClick: () => handleViewDocument(record),
                        style: matched ? { fontWeight: 'bold' } : notMatched ? { opacity: 0.6 } : {},
                    };
                }}
            />
            {viewerState.open && (
                <DocumentViewerModal
                    open={viewerState.open}
                    onClose={handleCloseViewer}
                    patientId={patient.id}
                    noteId={viewerState.noteId}
                    documentId={viewerState.documentId}
                    documentName={viewerState.documentName}
                />
            )}
        </>
    );
}
